
import React, { useEffect } from 'react';
import { Link, useLocation, NavLink } from 'react-router-dom';
import { Phone, Mail, Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../lib/LanguageContext';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export function Header() {
  const { t, lang, setLang } = useLanguage();
  const [isOpen, setIsOpen] = React.useState(false);
  const [isScrolled, setIsScrolled] = React.useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={cn(
      "sticky top-0 z-50 w-full transition-all duration-300",
      isScrolled 
        ? "bg-white/95 backdrop-blur shadow-lg py-2" 
        : "border-b border-slate-100 bg-white py-3 md:py-2 2xl:py-4"
    )}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center">
          {/* Top Row: Logo */}
          <div className={cn(
            "flex w-full items-center justify-between lg:justify-center transition-all duration-300 overflow-hidden",
            isScrolled ? "h-0 opacity-0 mb-0" : "h-28 md:h-36 lg:h-48 mb-2"
          )}>
            <Link to="/" className="h-20 md:h-28 lg:h-40 transition-all duration-300">
              <img 
                src="https://assets.auxodev.dk/assets/13397ece-935b-450f-937f-c5b4b345815d/2b0b318d-af9b-4b8f-80a7-50ddad590e7b.png" 
                alt="Vejrumbro Smede Maskinforretning ApS" 
                className="h-full w-auto transition-transform duration-300 hover:scale-105 object-contain" 
              />
            </Link>

            {/* Mobile Actions (Visible when NOT scrolled or moved to nav row if scrolled) */}
            <div className="flex items-center space-x-2 lg:hidden">
              <button
                onClick={() => setLang(lang === 'da' ? 'en' : 'da')}
                className="flex items-center space-x-1 rounded-md px-2 py-1 text-xs font-medium text-slate-600 border border-slate-200 hover:bg-slate-50"
              >
                <Globe className="h-3 w-3" />
                <span>{lang === 'da' ? 'EN' : 'DA'}</span>
              </button>
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center justify-center rounded-md p-2 text-slate-600 hover:bg-slate-50 hover:text-[#f59c01]"
              >
                {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {/* Desktop Middle Row: Nav, Language Switcher & Phone */}
          <div className={cn(
            "flex w-full items-center justify-between lg:justify-between transition-all duration-300",
            !isScrolled && "border-t border-slate-100 pt-4 md:pt-3 lg:pt-6"
          )}>
            <nav className="hidden lg:flex lg:items-center lg:justify-between lg:space-x-8 w-full">
              <div className="flex items-center space-x-8">
                {t.nav.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      cn(
                        "text-sm font-semibold tracking-wide uppercase transition-colors hover:text-[#f59c01]",
                        isActive ? "text-[#f59c01]" : "text-slate-600"
                      )
                    }
                  >
                    {item.name}
                  </NavLink>
                ))}
              </div>

              <div className="flex items-center space-x-6 ml-8 pl-8 border-l border-slate-100">
                <button
                  onClick={() => setLang(lang === 'da' ? 'en' : 'da')}
                  className="flex items-center space-x-2 rounded-full px-3 py-1 text-[10px] font-bold tracking-widest uppercase text-slate-500 border border-slate-200 hover:border-[#f59c01] hover:text-[#f59c01] transition-all"
                >
                  <Globe className="h-3 w-3" />
                  <span>{lang === 'da' ? 'EN' : 'DA'}</span>
                </button>
                
                <a
                  href={`tel:${t.phone.replace(/\s/g, '')}`}
                  className="flex items-center space-x-2 rounded-md bg-[#f59c01] px-4 py-2 text-sm font-bold text-white hover:bg-[#e08e01] transition-all shadow-sm"
                >
                  <Phone className="h-4 w-4" />
                  <span>{t.phone}</span>
                </a>
              </div>
            </nav>

            {/* Mobile Actions when scrolled */}
            {isScrolled && (
               <div className="flex items-center space-x-2 lg:hidden">
                <button
                  onClick={() => setLang(lang === 'da' ? 'en' : 'da')}
                  className="flex items-center space-x-1 rounded-md px-2 py-1 text-xs font-medium text-slate-600 border border-slate-200 hover:bg-slate-50"
                >
                  <Globe className="h-3 w-3" />
                  <span>{lang === 'da' ? 'EN' : 'DA'}</span>
                </button>
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="inline-flex items-center justify-center rounded-md p-2 text-slate-600 hover:bg-slate-50 hover:text-[#f59c01]"
                >
                  {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      {isOpen && (
        <div className="lg:hidden bg-white border-b border-slate-100">
          <div className="space-y-1 px-4 pb-3 pt-2">
            {t.nav.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "block rounded-md px-3 py-2 text-base font-medium",
                    isActive ? "bg-slate-50 text-[#f59c01]" : "text-slate-600 hover:bg-slate-50 hover:text-[#f59c01]"
                  )
                }
              >
                {item.name}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const { t } = useLanguage();
  return (
    <footer className="bg-slate-950 text-slate-400 py-12 border-t border-slate-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div>
            <Link to="/" className="inline-block mb-6">
              <img 
                src="https://assets.auxodev.dk/assets/13397ece-935b-450f-937f-c5b4b345815d/2b0b318d-af9b-4b8f-80a7-50ddad590e7b.png" 
                alt="Vejrumbro Smede Maskinforretning ApS" 
                className="h-24 w-auto transition-all duration-300 hover:scale-105 object-contain" 
              />
            </Link>
            <p className="text-sm leading-6">
              {t.address}<br />
              CVR: {t.cvr}
            </p>
          </div>
          <div>
            <h3 className="text-white font-bold text-lg mb-4">Kontakt</h3>
            <div className="space-y-2 text-sm">
              <a href={`tel:${t.phone.replace(/\s/g, '')}`} className="flex items-center hover:text-white transition-colors">
                <Phone className="h-4 w-4 mr-2 text-[#f59c01]" />
                {t.phone}
              </a>
              <a href={`mailto:${t.email}`} className="flex items-center hover:text-white transition-colors">
                <Mail className="h-4 w-4 mr-2 text-[#f59c01]" />
                {t.email}
              </a>
            </div>
          </div>
          <div>
            <h3 className="text-white font-bold text-lg mb-4">Service</h3>
            <ul className="space-y-2 text-sm">
              {t.nav.slice(1, 5).map((item) => (
                <li key={item.path}>
                  <Link to={item.path} className="hover:text-white transition-colors">{item.name}</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mt-12 pt-8 border-t border-slate-900 text-center text-xs">
          <p>© {new Date().getFullYear()} {t.company}. {t.ui.copyright}</p>
        </div>
      </div>
    </footer>
  );
}
